import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { 
  CreditCard, CurrencyBtc, ArrowLeft, Check, Copy,
  Lightning, QrCode, Timer, ArrowRight, X, Wallet, AppleLogo, GoogleLogo
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import BottomNav from '../../components/BottomNav';
import StripePayment from '../../components/StripePayment';
import WalletPayment from '../../components/WalletPayment';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TopUp = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('fiat');
  const [amount, setAmount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [cryptoRates, setCryptoRates] = useState({});
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');
  const [cryptoPayment, setCryptoPayment] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [copied, setCopied] = useState(false);

  const denominations = [10, 25, 50, 100, 200, 500];

  const cryptos = [
    { symbol: 'BTC', name: 'Bitcoin', color: '#F7931A', icon: '₿' },
    { symbol: 'ETH', name: 'Ethereum', color: '#627EEA', icon: 'Ξ' },
    { symbol: 'USDC', name: 'USD Coin', color: '#2775CA', icon: '$' },
    { symbol: 'SOL', name: 'Solana', color: '#9945FF', icon: '◎' },
    { symbol: 'IKA', name: 'IKA Coin', color: '#C3A365', icon: '◆', native: true }
  ];

  useEffect(() => {
    fetchCryptoRates();
  }, []);

  const fetchCryptoRates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/crypto/rates`);
      setCryptoRates(data.rates || {});
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    }
  };

  const handleFiatTopUp = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/payments/create-intent`,
        { amount, currency: 'gbp' },
        { withCredentials: true }
      );
      
      setPaymentIntent(data);
      
      // In production with Stripe Elements, you'd redirect to Stripe
      // For now, show the mock payment info
      toast.success(language === 'fr' 
        ? 'Intention de paiement créée' 
        : 'Payment intent created');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCryptoTopUp = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/crypto/create-payment`,
        { amount, crypto_currency: selectedCrypto },
        { withCredentials: true }
      );
      
      setCryptoPayment(data);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  const confirmCryptoPayment = async () => {
    if (!cryptoPayment) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/crypto/payment/${cryptoPayment.payment_id}/confirm`,
        {},
        { withCredentials: true }
      );
      
      toast.success(language === 'fr' 
        ? `+£${data.amount} crédité sur votre compte!` 
        : `+£${data.amount} credited to your account!`);
      
      setCryptoPayment(null);
      navigate('/wallet');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const confirmFiatPayment = async () => {
    if (!paymentIntent) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/payments/confirm`,
        { payment_intent_id: paymentIntent.payment_intent_id },
        { withCredentials: true }
      );
      
      toast.success(language === 'fr' 
        ? `+£${data.amount} crédité!` 
        : `+£${data.amount} credited!`);
      
      setPaymentIntent(null);
      navigate('/wallet');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Confirmation failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success(language === 'fr' ? 'Copié!' : 'Copied!');
  };

  const getCryptoAmount = () => {
    if (!cryptoRates[selectedCrypto]) return '...';
    return (amount / cryptoRates[selectedCrypto].rate).toFixed(8);
  };

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white pb-24">
      {/* Header */}
      <div className="px-5 pt-8 pb-4">
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-[#1E2F26] flex items-center justify-center"
          >
            <ArrowLeft size={20} className="text-white" />
          </button>
          <div>
            <h1 className="text-2xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {language === 'fr' ? 'Recharger' : 'Top Up'}
            </h1>
            <p className="text-[#8BA89A] text-sm">
              {language === 'fr' ? 'Ajoutez du crédit à votre carte' : 'Add credit to your card'}
            </p>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex bg-[#1E2F26] p-1.5 rounded-xl mb-6">
          <button
            onClick={() => { setActiveTab('fiat'); setCryptoPayment(null); setPaymentIntent(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'fiat' ? 'bg-[#C3A365] text-[#0F1A14]' : 'text-[#8BA89A]'
            }`}
            data-testid="topup-tab-fiat"
          >
            <CreditCard size={18} />
            <span className="text-sm">{language === 'fr' ? 'Carte' : 'Card'}</span>
          </button>
          <button
            onClick={() => { setActiveTab('crypto'); setCryptoPayment(null); setPaymentIntent(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-all ${
              activeTab === 'crypto' ? 'bg-[#C3A365] text-[#0F1A14]' : 'text-[#8BA89A]'
            }`}
            data-testid="topup-tab-crypto"
          >
            <CurrencyBtc size={18} />
            <span className="text-sm">Crypto</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-5">
        <AnimatePresence mode="wait">
          {/* FIAT TAB */}
          {activeTab === 'fiat' && !paymentIntent && (
            <motion.div
              key="fiat-select"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Amount Selection */}
              <div className="mb-8">
                <p className="text-[#8BA89A] text-sm mb-4">
                  {language === 'fr' ? 'Montant' : 'Amount'}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {denominations.map((d) => (
                    <button
                      key={d}
                      onClick={() => setAmount(d)}
                      className={`py-4 rounded-xl font-medium transition-all ${
                        amount === d
                          ? 'bg-[#C3A365] text-[#0F1A14]'
                          : 'bg-[#1E2F26] text-white border border-[#283D32] hover:border-[#C3A365]/50'
                      }`}
                      data-testid={`amount-${d}`}
                    >
                      £{d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Amount */}
              <div className="mb-8">
                <p className="text-[#8BA89A] text-sm mb-2">
                  {language === 'fr' ? 'Ou montant personnalisé' : 'Or custom amount'}
                </p>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8BA89A] text-xl">£</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(Math.min(10000, Math.max(5, parseInt(e.target.value) || 0)))}
                    className="w-full bg-[#1E2F26] border border-[#283D32] rounded-xl pl-10 pr-4 py-4 text-white text-xl focus:outline-none focus:border-[#C3A365]/50"
                    min="5"
                    max="10000"
                    data-testid="custom-amount-input"
                  />
                </div>
                <p className="text-[#8BA89A] text-xs mt-2">
                  Min £5 · Max £10,000
                </p>
              </div>

              {/* Payment Button */}
              <motion.button
                onClick={handleFiatTopUp}
                disabled={loading || amount < 5}
                className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="fiat-topup-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#0F1A14] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <CreditCard size={20} />
                    {language === 'fr' ? `Payer £${amount}` : `Pay £${amount}`}
                  </>
                )}
              </motion.button>

              {/* Divider */}
              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-[#283D32]" />
                <span className="text-[#8BA89A] text-xs uppercase tracking-wider">
                  {language === 'fr' ? 'ou paiement express' : 'or express payment'}
                </span>
                <div className="flex-1 h-px bg-[#283D32]" />
              </div>

              {/* Apple Pay / Google Pay */}
              <WalletPayment
                amount={amount}
                currency="gbp"
                label={language === 'fr' ? 'Recharge habitué' : 'habitué Top-up'}
                language={language}
                onSuccess={(result) => {
                  toast.success(language === 'fr' 
                    ? `+£${amount} crédité via wallet!` 
                    : `+£${amount} credited via wallet!`);
                  navigate('/wallet');
                }}
                onError={(error) => {
                  toast.error(error.message || (language === 'fr' ? 'Paiement échoué' : 'Payment failed'));
                }}
              />

              <p className="text-center text-[#8BA89A] text-xs mt-4">
                {language === 'fr' 
                  ? 'Paiement sécurisé via Stripe' 
                  : 'Secure payment via Stripe'}
              </p>
            </motion.div>
          )}

          {/* FIAT PAYMENT INTENT - Stripe Elements Integration */}
          {activeTab === 'fiat' && paymentIntent && (
            <motion.div
              key="fiat-confirm"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 rounded-full bg-[#C3A365]/20 flex items-center justify-center mx-auto mb-4">
                  <CreditCard size={40} className="text-[#C3A365]" />
                </div>

                <h2 className="text-2xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {language === 'fr' ? 'Paiement sécurisé' : 'Secure Payment'}
                </h2>

                <p className="text-4xl text-[#C3A365] font-light mb-4">
                  £{paymentIntent.amount}
                </p>
              </div>

              {/* Stripe Payment Component */}
              <StripePayment
                amount={paymentIntent.amount}
                clientSecret={paymentIntent.client_secret}
                language={language}
                onSuccess={(result) => {
                  toast.success(language === 'fr' 
                    ? `+£${paymentIntent.amount} crédité!` 
                    : `+£${paymentIntent.amount} credited!`);
                  setPaymentIntent(null);
                  navigate('/wallet');
                }}
                onError={(error) => {
                  toast.error(error.message || (language === 'fr' ? 'Paiement échoué' : 'Payment failed'));
                }}
              />

              {/* Cancel button */}
              <button
                onClick={() => setPaymentIntent(null)}
                className="w-full mt-4 py-3 border border-[#3E5248] text-[#8BA89A] rounded-xl font-medium hover:border-[#C3A365]/50 transition-colors"
                data-testid="cancel-fiat-btn"
              >
                {language === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
            </motion.div>
          )}

          {/* CRYPTO TAB */}
          {activeTab === 'crypto' && !cryptoPayment && (
            <motion.div
              key="crypto-select"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {/* Crypto Selection */}
              <div className="mb-6">
                <p className="text-[#8BA89A] text-sm mb-4">
                  {language === 'fr' ? 'Choisir une crypto' : 'Select cryptocurrency'}
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {cryptos.map((crypto) => (
                    <button
                      key={crypto.symbol}
                      onClick={() => setSelectedCrypto(crypto.symbol)}
                      className={`py-3 rounded-xl transition-all flex flex-col items-center gap-1 relative ${
                        selectedCrypto === crypto.symbol
                          ? 'ring-2 ring-[#C3A365]'
                          : 'bg-[#1E2F26] border border-[#283D32]'
                      }`}
                      style={selectedCrypto === crypto.symbol ? { backgroundColor: `${crypto.color}20` } : {}}
                      data-testid={`crypto-${crypto.symbol}`}
                    >
                      {crypto.native && (
                        <span className="absolute -top-1 -right-1 bg-[#C3A365] text-[#0F1A14] text-[8px] px-1.5 py-0.5 rounded-full font-bold">
                          {language === 'fr' ? 'NATIF' : 'NATIVE'}
                        </span>
                      )}
                      <span className="text-xl" style={{ color: crypto.color }}>{crypto.icon}</span>
                      <span className="text-xs text-white">{crypto.symbol}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount Selection */}
              <div className="mb-6">
                <p className="text-[#8BA89A] text-sm mb-4">
                  {language === 'fr' ? 'Montant en GBP' : 'Amount in GBP'}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {[25, 50, 100, 250, 500, 1000].map((d) => (
                    <button
                      key={d}
                      onClick={() => setAmount(d)}
                      className={`py-3 rounded-xl font-medium transition-all text-sm ${
                        amount === d
                          ? 'bg-[#C3A365] text-[#0F1A14]'
                          : 'bg-[#1E2F26] text-white border border-[#283D32]'
                      }`}
                    >
                      £{d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conversion Preview */}
              <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#8BA89A] text-xs mb-1">
                      {language === 'fr' ? 'Vous payez' : 'You pay'}
                    </p>
                    <p className="text-2xl text-white font-light">
                      {getCryptoAmount()} <span className="text-[#8BA89A] text-sm">{selectedCrypto}</span>
                    </p>
                  </div>
                  <ArrowRight size={24} className="text-[#8BA89A]" />
                  <div className="text-right">
                    <p className="text-[#8BA89A] text-xs mb-1">
                      {language === 'fr' ? 'Vous recevez' : 'You receive'}
                    </p>
                    <p className="text-2xl text-[#C3A365] font-light">
                      £{amount}
                    </p>
                  </div>
                </div>
                {cryptoRates[selectedCrypto] && (
                  <p className="text-[#8BA89A] text-xs mt-3 text-center">
                    1 {selectedCrypto} = £{cryptoRates[selectedCrypto].rate.toLocaleString()}
                  </p>
                )}
              </div>

              {/* Payment Button */}
              <motion.button
                onClick={handleCryptoTopUp}
                disabled={loading || amount < 10}
                className="w-full py-4 bg-[#C3A365] text-[#0F1A14] font-semibold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="crypto-topup-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-[#0F1A14] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <CurrencyBtc size={20} />
                    {language === 'fr' ? 'Générer l\'adresse' : 'Generate Address'}
                  </>
                )}
              </motion.button>
            </motion.div>
          )}

          {/* CRYPTO PAYMENT */}
          {activeTab === 'crypto' && cryptoPayment && (
            <motion.div
              key="crypto-payment"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${cryptos.find(c => c.symbol === cryptoPayment.crypto_currency)?.color}20` }}>
                  <span className="text-3xl" style={{ color: cryptos.find(c => c.symbol === cryptoPayment.crypto_currency)?.color }}>
                    {cryptos.find(c => c.symbol === cryptoPayment.crypto_currency)?.icon}
                  </span>
                </div>
                <h2 className="text-xl font-light mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {language === 'fr' ? 'Envoyez' : 'Send'}
                </h2>
                <p className="text-3xl text-[#C3A365] font-light">
                  {cryptoPayment.amount_crypto} {cryptoPayment.crypto_currency}
                </p>
                <p className="text-[#8BA89A] text-sm">≈ £{cryptoPayment.amount_gbp}</p>
              </div>

              {/* QR Code - Real QR for wallet address */}
              <div className="bg-white rounded-2xl p-4 mb-4 mx-auto max-w-[200px]" data-testid="crypto-qr-code">
                <QRCodeSVG
                  value={cryptoPayment.wallet_address}
                  size={168}
                  level="H"
                  marginSize={0}
                  bgColor="#FFFFFF"
                  fgColor="#0F1A14"
                />
              </div>

              {/* Wallet Address */}
              <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4 mb-4">
                <p className="text-[#8BA89A] text-xs mb-2">
                  {language === 'fr' ? 'Adresse du wallet' : 'Wallet Address'}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-white text-xs font-mono flex-1 break-all">
                    {cryptoPayment.wallet_address}
                  </p>
                  <button
                    onClick={() => copyToClipboard(cryptoPayment.wallet_address)}
                    className="p-2 bg-[#283D32] rounded-lg"
                    data-testid="copy-wallet-address-btn"
                  >
                    {copied ? (
                      <Check size={18} className="text-[#C3A365]" />
                    ) : (
                      <Copy size={18} className="text-white" />
                    )}
                  </button>
                </div>
              </div>

              {/* Timer */}
              <div className="flex items-center justify-center gap-2 text-[#8BA89A] mb-6">
                <Timer size={16} />
                <span className="text-sm">
                  {language === 'fr' ? 'Expire dans 30 minutes' : 'Expires in 30 minutes'}
                </span>
              </div>

              {/* Status */}
              <div className="bg-[#1E2F26]/50 border border-dashed border-[#283D32] rounded-xl p-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-yellow-500 animate-pulse" />
                  <div>
                    <p className="text-white text-sm">
                      {language === 'fr' ? 'En attente de paiement' : 'Awaiting payment'}
                    </p>
                    <p className="text-[#8BA89A] text-xs">
                      {cryptoPayment.mode === 'mock' 
                        ? (language === 'fr' ? 'Mode démo - Cliquez pour simuler' : 'Demo mode - Click to simulate')
                        : (language === 'fr' ? 'Envoyez le montant exact' : 'Send exact amount')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setCryptoPayment(null)}
                  className="flex-1 py-4 border border-[#3E5248] text-white rounded-xl font-medium"
                >
                  {language === 'fr' ? 'Annuler' : 'Cancel'}
                </button>
                {cryptoPayment.mode === 'mock' && (
                  <button
                    onClick={confirmCryptoPayment}
                    disabled={loading}
                    className="flex-1 py-4 bg-[#C3A365] text-[#0F1A14] rounded-xl font-semibold flex items-center justify-center gap-2"
                    data-testid="confirm-crypto-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-[#0F1A14] border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Lightning size={20} weight="fill" />
                        {language === 'fr' ? 'Simuler' : 'Simulate'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <BottomNav />
    </div>
  );
};

export default TopUp;
